<allas-main-layout>
  <allas-margin-wrapper>
    <!-- Product Detail Content -->
    @if (loading()) {
    <!-- Loading Skeleton -->
    <div class="overflow-hidden my-4 lg:my-12">
      <div class="flex flex-col lg:flex-row gap-4 animate-pulse">
        <div class="h-64 lg:h-96 bg-gray-200 rounded-lg w-full lg:w-1/3"></div>
        <div class="w-full lg:w-2/3 flex flex-col gap-2">
          <div class="h-24 bg-gray-200 rounded-lg"></div>
          <div class="h-24 bg-gray-200 rounded-lg"></div>
          <div class="h-24 bg-gray-200 rounded-lg"></div>
          <div class="h-16 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    </div>
    } @else { @if (product(); as currentProduct) {
    <div class="overflow-hidden mt-0 lg:mt-6">
      <!-- Mobile Back Button -->
      <div class="lg:hidden p-4 border-b border-gray-200">
        <button
          (click)="goBack()"
          class="flex items-center text-gray-600 hover:text-gray-900 fs-condensed"
        >
          <lucide-icon [name]="chevronLeftIcon" class="w-5 h-5 mr-2" />
          Volver
        </button>
      </div>

      <!-- Breadcrumb -->
      <div
        class="relative mb-2 p-2 pt-3 lg:mb-4 lg:p-2 lg:pt-0 fs-condensed"
        id="breadcrumb"
      >
        <div
          class="flex items-center text-xs lg:text-sm text-gray-600 overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-hide scroll-smooth snap-x snap-mandatory lg:flex-wrap lg:overflow-visible lg:gap-y-2 pr-12 lg:pr-0"
          style="scrollbar-width: none; -ms-overflow-style: none"
        >
          <a routerLink="/" class="hover:text-allasRed shrink-0 snap-start">
            Inicio
          </a>
          <svg
            class="w-3 h-3 lg:w-4 lg:h-4 mx-1.5 lg:mx-2 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
          <a
            routerLink="/repuestos"
            class="hover:text-allasRed shrink-0 snap-start"
          >
            Repuestos
          </a>
          @for (breadcrumb of breadcrumbs(); track breadcrumb.id) {
          <svg
            class="w-3 h-3 lg:w-4 lg:h-4 mx-1.5 lg:mx-2 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
          <a
            [routerLink]="['/repuestos', breadcrumb.slug]"
            class="hover:text-allasRed shrink-0 snap-start"
          >
            {{ breadcrumb.name }}
          </a>
          }
        </div>
        <div
          class="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none lg:hidden flex items-center justify-end pr-2"
        ></div>
      </div>

      <div class="flex flex-col lg:flex-row lg:items-start xl:items-center">
        <!-- Left Column: Product Images -->
        <div class="w-full lg:w-1/3 p-2 px-4 lg:border-r">
          <!-- Main Image -->
          <div class="mb-3 lg:mb-4">
            <div class="aspect-square bg-gray-50 rounded-lg overflow-hidden">
              @if (currentProduct.imagenes.xl.length > 0) {
              <app-image-zoom
                [imageSrc]="
                  selectedImage() ||
                  currentProduct.imagenes.xl[0] ||
                  '/placeholder.svg'
                "
                [imageAlt]="currentProduct.descripcion"
                [zoomLevel]="1.75"
                [minZoom]="0.5"
                [maxZoom]="9"
                width="w-full"
                height="h-full"
              ></app-image-zoom>
              }
            </div>
            <p
              class="hidden lg:block text-xs text-gray-500 mt-2 text-center fs-condensed"
            >
              <lucide-icon [name]="zoomInIcon" class="w-4 h-4 inline mr-1" />
              Pasa el cursor sobre la imagen para ampliar • Usa el scroll para
              ajustar el zoom
            </p>
          </div>

          <!-- Thumbnail Images -->
          @if (currentProduct.imagenes && currentProduct.imagenes.xl.length > 1)
          {
          <div class="flex gap-2 justify-center overflow-x-auto pb-2 lg:pb-0">
            @for (image of currentProduct.imagenes.xl.slice(0, 6); track $index)
            {
            <button
              (click)="selectImage(image)"
              class="w-14 h-14 lg:w-16 lg:h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 shrink-0"
              [ngClass]="{
                'border-allasRed': selectedImage() === image,
                'border-gray-200 hover:border-gray-300':
                  selectedImage() !== image
              }"
            >
              <img
                [src]="image"
                [alt]="currentProduct.descripcion"
                class="w-full h-full object-contain"
              />
            </button>
            }
          </div>
          }
        </div>

        <!-- Right Column: Product Details -->
        <div
          class="w-full lg:w-2/3 p-3 px-4 lg:p-2 lg:px-4 border-t lg:border-t-0 border-gray-200"
        >
          <!-- Product Title -->
          <h1
            class="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 mb-2 lg:mb-2 fs-condensed leading-tight"
          >
            {{ currentProduct.nombre_web }}
            @if (vehiculoTextFromSlug()) { para {{ vehiculoTextFromSlug() }} }
          </h1>

          <!-- Brand Link -->
          <a
            [routerLink]="['/productos/marca', toSlug(currentProduct.marca)]"
            class="inline-block text-allasRed hover:text-allasRed/75 underline mb-3 lg:mb-4 fs-condensed text-sm"
          >
            Ver todos los {{ currentProduct.marca }}
          </a>

          <!-- Product Identifiers -->
          <div
            class="space-y-1.5 lg:space-y-2 mb-3 lg:mb-4 text-xs lg:text-sm text-gray-600 fs-condensed"
          >
            <p><strong>Parte #:</strong> {{ currentProduct.part_number }}</p>
            <p><strong>Código #:</strong> {{ currentProduct.sku.trim() }}</p>
            @if (warrantyYears() > 0) {
            <div>
              <allas-warranty-guard
                [months]="warrantyYears() * 12"
              ></allas-warranty-guard>
            </div>
            }
          </div>

          <!-- Vehicle Compatibility -->
          <div class="mb-3 lg:mb-4 w-full lg:w-max">
            <allas-vehicle-compatibility
              [status]="
                !defaultVehicle()
                  ? 'not-selected'
                  : loadingVehicleCompatibility()
                  ? 'loading'
                  : myVehicleFitsWithProduct()
                  ? 'compatible'
                  : 'incompatible'
              "
              [defaultVehicle]="defaultVehicle() || 'Vehículo no seleccionado'"
            ></allas-vehicle-compatibility>
          </div>

          <!-- Delivery Options & Price (with live data transition) -->
          <div
            class="transition-opacity duration-500 ease-in-out"
            [class.opacity-50]="!liveDataLoaded()"
            [class.animate-pulse]="!liveDataLoaded()"
          >
            @if (currentProduct.precio > 0) {
            <div class="mb-4 lg:mb-4">
              <allas-delivery-type-by-store
                [product]="currentProduct"
              ></allas-delivery-type-by-store>
            </div>
            }

            <!-- Price -->
            <div class="flex flex-col gap-2 mb-4 lg:mb-0">
              <div class="scale-110 origin-left lg:scale-100">
                <allas-product-price
                  [price]="currentProduct.precio"
                ></allas-product-price>
              </div>
              @if (currentProduct.precio > 0) { @if (currentProduct.subcategoria
              === 'bateria') {
              <allas-core-deposit [product]="currentProduct"></allas-core-deposit>
              } }
            </div>
          </div>

          <!-- Quantity Selector -->
          <div class="mb-4 lg:mb-6 lg:w-max">
            <label
              class="block text-sm font-medium text-gray-700 mb-2 lg:mb-2 fs-condensed"
            >
              Cantidad:
            </label>
            <div class="flex items-center gap-2 w-full lg:w-auto">
              <button
                (click)="decreaseQuantity()"
                [disabled]="quantity() <= 1"
                class="size-10 lg:size-8 text-base lg:text-sm rounded-lg lg:rounded-sm border-2 lg:border border-gray-300 flex items-center justify-center hover:bg-allasRed/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shrink-0"
              >
                -
              </button>
              <input
                id="quantity"
                name="quantity"
                type="number"
                [(ngModel)]="quantity"
                min="1"
                class="flex-1 lg:w-12 h-10 lg:h-8 text-base lg:text-sm text-center border-2 lg:border border-gray-300 rounded-lg lg:rounded-sm focus:ring-2 lg:focus:ring-1 focus:ring-allasRed focus:border-allasRed outline-none transition-all duration-200 font-semibold lg:font-normal"
              />
              <button
                (click)="increaseQuantity()"
                class="size-10 lg:size-8 text-base lg:text-sm rounded-lg lg:rounded-sm border-2 lg:border border-gray-300 flex items-center justify-center hover:bg-allasRed/10 transition-colors duration-200 shrink-0"
              >
                +
              </button>
            </div>
          </div>

          <!-- Mobile: Add to Cart Button -->
          <div class="lg:hidden mb-4">
            <button
              (click)="addToCart()"
              class="w-full bg-appPrimary-500 hover:bg-appPrimary-600 active:bg-appPrimary-700 text-white py-4 px-6 rounded-xl font-bold text-base transition-colors flex items-center justify-center space-x-2 shadow-lg shadow-appPrimary-500/20"
            >
              <lucide-icon [name]="shoppingCartIcon" class="w-6 h-6" />
              <span class="fs-condensed">AGREGAR AL CARRITO</span>
            </button>

            <!-- Mobile: Action Buttons -->
            <div
              class="flex items-center justify-center gap-8 pt-4 border-b border-gray-200 pb-4"
            >
              <button
                (click)="showEspecificationsDialog = true"
                class="flex flex-col items-center gap-1 text-gray-700 hover:text-appPrimary-500 transition-colors fs-condensed"
              >
                <lucide-icon [name]="infoIcon" class="size-6" />
                <span class="text-xs">Especificaciones</span>
              </button>
              <button
                (click)="showShareDialog = true"
                class="flex flex-col items-center gap-1 text-gray-700 hover:text-appPrimary-500 transition-colors fs-condensed"
              >
                <lucide-icon [name]="shareIcon" class="size-6" />
                <span class="text-xs">Compartir</span>
              </button>
            </div>
          </div>

          <!-- Desktop: Footer with Actions -->
          <footer class="hidden lg:flex justify-between items-center">
            <!-- Add to Cart Button -->
            <button
              (click)="addToCart()"
              class="bg-appPrimary-500 hover:bg-appPrimary-600 text-white py-3 px-6 rounded font-semibold text-base transition-colors flex items-center justify-center space-x-2"
            >
              <lucide-icon [name]="shoppingCartIcon" class="w-6 h-6" />
              <span class="fs-condensed">AGREGAR AL CARRITO</span>
            </button>

            <div class="flex items-center gap-4 pb-14">
              <!-- Especificaciones Button -->
              <button
                (click)="showEspecificationsDialog = true"
                class="cursor-pointer flex items-center gap-2 hover:underline text-sm fs-condensed"
              >
                <lucide-icon [name]="infoIcon" class="size-4" />
                ESPECIFICACIONES
              </button>
              <!-- Compartir Button -->
              <button
                (click)="showShareDialog = true"
                class="cursor-pointer flex items-center gap-2 hover:underline text-sm fs-condensed text-gray-700 hover:text-appPrimary-500 transition-colors"
              >
                <lucide-icon [name]="shareIcon" class="size-4" />
                COMPARTIR
              </button>
            </div>
          </footer>
        </div>
      </div>

      <!-- Product Specifications, Description and Buyer's Guide -->
      <div class="border-t border-gray-200 p-4 lg:p-8 mt-4 lg:mt-0">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <section>
            <!-- Buyer's Guide -->
            <div>
              <h3
                class="text-lg lg:text-xl font-bold text-gray-900 mb-3 lg:mb-4 fs-condensed"
              >
                VEHÍCULOS A LOS QUE APLICA
              </h3>
              <allas-vehicle-guide-modal
                [vehicleGuideTree]="currentProduct.guia_tree || []"
              ></allas-vehicle-guide-modal>
            </div>
          </section>

          <!-- Description and Product Reviews -->
          <section>
            <!-- Product Description -->
            <div class="fs-condensed">
              <h3
                class="text-lg lg:text-xl font-bold text-gray-900 mb-3 lg:mb-4"
              >
                DESCRIPCIÓN DEL PRODUCTO
              </h3>
              <div class="text-sm lg:text-base text-gray-700 leading-relaxed">
                @if (currentProduct.seoDescription) {
                <p class="fs-condensed text-gray-900 w-full">
                  {{ descriptionSeoOrganizada().title }}
                </p>
                <small>
                  {{ descriptionSeoOrganizada().description }}
                </small>

                @if (descriptionSeoOrganizada().bullets.length > 0) {
                <ul class="list-disc list-inside mt-2">
                  @for (bullet of descriptionSeoOrganizada().bullets; track
                  bullet) {
                  <li class="ml-2">• {{ bullet }}</li>
                  }
                </ul>
                }
                <!---->
                } @else {
                <span
                  [innerHTML]="
                    productUtilsService.getSeoDescription(currentProduct)
                  "
                ></span>
                }
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
    } @else {
    <!-- Product Not Found -->
    <div class="text-center py-12 lg:py-16 px-4">
      <div class="text-5xl lg:text-6xl mb-4">🔍</div>
      <h2 class="text-xl lg:text-2xl font-bold text-gray-900 mb-3 lg:mb-4">
        Producto no encontrado
      </h2>
      <p class="text-sm lg:text-base text-gray-600 mb-4 lg:mb-6">
        El producto que buscas no existe o ha sido removido.
      </p>
      <a
        routerLink="/repuestos"
        class="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
      >
        Ver todos los productos
      </a>
    </div>
    } }
  </allas-margin-wrapper>
</allas-main-layout>

<p-dialog
  header="ESPECIFICACIONES DEL PRODUCTO"
  [(visible)]="showEspecificationsDialog"
  [modal]="true"
  [dismissableMask]="true"
  [style]="{ width: '600px' }"
>
  <div class="space-y-2 fs-condensed">
    <div class="flex justify-between py-2 border-b border-gray-100 bg-gray-50">
      <span class="font-medium text-gray-700">Parte #</span>
      <span class="text-gray-900">
        {{ product()?.part_number?.trim() ?? "" }}
      </span>
    </div>
    @for (attribute of attributesToDisplay(); track attribute.label) {
    <div class="flex justify-between py-2 border-b border-gray-100">
      <span class="font-medium text-gray-700">{{ attribute.label }}</span>
      <span class="text-gray-900">{{ attribute.value }}</span>
    </div>
    }

    <div class="flex justify-between py-2 border-b border-gray-100 bg-gray-50">
      <span class="font-medium text-gray-700">Código #</span>
      <span class="text-gray-900">
        {{ product()?.sku?.trim() ?? "" }}
      </span>
    </div>
  </div>
</p-dialog>

<!-- Share Dialog -->
<p-dialog
  header="COMPARTIR PRODUCTO"
  [(visible)]="showShareDialog"
  [modal]="true"
  [dismissableMask]="true"
  [style]="{ width: '400px' }"
>
  <div class="fs-condensed">
    <div class="space-y-2">
      <!-- WhatsApp -->
      <button
        (click)="shareOnWhatsApp()"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200 group"
      >
        <div
          class="size-10 rounded-full bg-[#25D366] flex items-center justify-center shrink-0"
        >
          <svg
            class="w-6 h-6 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
            />
          </svg>
        </div>
        <span class="font-medium text-gray-700 group-hover:text-gray-900">
          WhatsApp
        </span>
      </button>

      <!-- Facebook -->
      <button
        (click)="shareOnFacebook()"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200 group"
      >
        <div
          class="size-10 rounded-full bg-[#1877F2] flex items-center justify-center shrink-0"
        >
          <svg
            class="w-6 h-6 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
            />
          </svg>
        </div>
        <span class="font-medium text-gray-700 group-hover:text-gray-900">
          Facebook
        </span>
      </button>

      <!-- Twitter/X -->
      <button
        (click)="shareOnTwitter()"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200 group"
      >
        <div
          class="size-10 rounded-full bg-[#000000] flex items-center justify-center shrink-0"
        >
          <svg
            class="w-5 h-5 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
            />
          </svg>
        </div>
        <span class="font-medium text-gray-700 group-hover:text-gray-900">
          Twitter / X
        </span>
      </button>

      <!-- Copiar enlace -->
      <button
        (click)="copyLink()"
        class="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors border border-gray-200 group"
        [ngClass]="{
          'bg-green-50 border-green-200': linkCopied(),
          'hover:bg-gray-50': !linkCopied()
        }"
      >
        <div
          class="size-10 rounded-full flex items-center justify-center shrink-0 transition-colors"
          [ngClass]="{
            'bg-green-500': linkCopied(),
            'bg-gray-200 group-hover:bg-gray-300': !linkCopied()
          }"
        >
          @if (linkCopied()) {
          <lucide-icon [name]="checkIcon" class="size-5 text-white" />
          } @else {
          <svg
            class="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          }
        </div>
        <span
          class="font-medium transition-colors"
          [ngClass]="{
            'text-green-700': linkCopied(),
            'text-gray-700 group-hover:text-gray-900': !linkCopied()
          }"
        >
          {{ linkCopied() ? "¡Enlace copiado!" : "Copiar enlace" }}
        </span>
      </button>
    </div>
  </div>
</p-dialog>
